import { Property } from '@/types';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { setTranslationInState, removeTranslationInState } from '../utils/translate';
// eslint-disable-next-line import/no-cycle
import { refreshImageVmlOnLinkChange, refreshButtonVmlOnLinkChange } from './index';

/**
 * Extract the base64-encoded redirect ID from a href value.
 * Supports both #redirect_XXX and #spin_redirect_XXX formats.
 */
const extractRedirectId = (href: string): string => {
  if (href.includes('#spin_redirect_')) {
    return href.split('#spin_redirect_')[1] || '';
  }
  if (href.includes('#redirect_')) {
    return href.split('#redirect_')[1] || '';
  }
  return '';
};

export const setLink = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector)?.firstElementChild as HTMLElement;
  const isHeading = element && ['H1', 'H2', 'H3', 'H4'].includes(element.tagName);
  let link: HTMLAnchorElement = document.createElement('a');

  if (element) {
    if (isHeading) {
      // For headings, we want: <hX><a>content</a></hX> instead of <a><hX>content</hX></a>
      const existingLink = element.querySelector('a');
      if (existingLink) {
        link = existingLink.cloneNode(true) as HTMLAnchorElement;
      } else {
        link.innerHTML = element.innerHTML;
      }

      if (property.value) {
        link.setAttribute('href', property.value);
        if (property.value?.includes('redirect_')) {
          link.setAttribute('data-redirect', extractRedirectId(property.value));
        } else {
          link.removeAttribute('data-redirect');
        }
        element.innerHTML = '';
        element.appendChild(link);
        setTranslationInState(link, 'link', property.name);
      } else {
        // Remove link - restore original content
        if (existingLink) {
          const key = existingLink.getAttribute('data-spmtranslationid-href');
          if (key) {
            removeTranslationInState(key.replace('LANG_', ''));
          }
          element.innerHTML = existingLink.innerHTML;
        }
      }
    } else {
      // Original behavior for non-heading elements
      if (element.tagName === 'A') {
        link = element.cloneNode(true) as HTMLAnchorElement;
      } else {
        link.innerHTML = element.outerHTML;
      }

      if (property.value) {
        link.setAttribute('href', property.value);
        if (property.value?.includes('redirect_')) {
          link.setAttribute('data-redirect', extractRedirectId(property.value));
        } else {
          link.removeAttribute('data-redirect');
        }
        element.replaceWith(link);
        setTranslationInState(link, 'link', property.name);
      } else {
        // Remove link
        element.replaceWith(link.firstElementChild as HTMLElement);
        if (link) {
          const key = link.getAttribute('data-spmtranslationid-href');
          if (key) {
            removeTranslationInState(key.replace('LANG_', ''));
          }
        }
      }
    }

    // Refresh VML for image widgets if border-radius is active
    const parentSelector = template?.querySelector(selector);
    if (parentSelector) {
      const thElement = parentSelector.closest('th') || parentSelector.querySelector('th');
      if (thElement) {
        if (thElement.querySelector('img')) {
          refreshImageVmlOnLinkChange(thElement as HTMLElement);
        }
        // Refresh VML for button widgets if border-radius is active
        const buttonTable = thElement.closest('table.spm_button') || thElement.closest('table.button');
        if (buttonTable) {
          refreshButtonVmlOnLinkChange(thElement as HTMLElement);
        }
      }
    }
  }
};

export const setDataRedirectAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    if (property.value?.includes('redirect_')) {
      element.setAttribute('data-redirect', extractRedirectId(property.value));
    } else {
      element.removeAttribute('data-redirect'); // Remove this attribute if is not a template redirection
    }
  }
};

export const setDataNextStepAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    if (property.value?.includes('redirect_')) {
      element.setAttribute('data-redirect', extractRedirectId(property.value));
    } else if (property.value?.includes('send-form_')) {
      const value = property.value?.replace('#send-form_', '');
      if (value === 'close') {
        element.removeAttribute('data-redirect');
        element.removeAttribute('data-to-url');
      } else {
        try {
          const decoded = atob(value);
          if (decoded.startsWith('toUrl:')) {
            const url = btoa(decoded.replace('toUrl:', ''));
            element.setAttribute('data-to-url', url);
            element.removeAttribute('data-redirect');
          }
        } catch (e) {
          element.removeAttribute('data-redirect');
          element.removeAttribute('data-to-url');
        }
      }
    } else {
      element.removeAttribute('data-redirect');
      element.removeAttribute('data-to-url');
    }
  }
};

/**
 * Setter for push notification link.
 * Stores redirect IDs as base64 only, and URLs as-is in data-redirect.
 */
export const setPushLinkAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    if (!property.value) {
      element.removeAttribute('data-redirect');
    } else if (property.value.includes('redirect_')) {
      element.setAttribute('data-redirect', extractRedirectId(property.value));
    } else {
      element.setAttribute('data-redirect', property.value);
    }
  }
};

export const setDataSendFormAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    if (property.value === true) {
      element.setAttribute('data-send-form', 'true');
    } else {
      element.removeAttribute('data-send-form');
    }
  }
};
